import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Skeleton } from "@/components/Skeleton";
import { Avatar } from "@/components/Avatar";
import { useEffortUnit, formatEffort } from "@/lib/effort";
import { parseDateLocal } from "@/lib/format";

const WEEKS = 8;
const DAY = 86_400_000;

function mondayOf(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}
function weekKey(d: Date): string {
  const w = mondayOf(d);
  return `${w.getFullYear()}-${String(w.getMonth() + 1).padStart(2, "0")}-${String(w.getDate()).padStart(2, "0")}`;
}

/**
 * Workload balancing — a per-member, per-week committed-effort heatmap built
 * from open tasks across the user's projects, with overloaded cells flagged
 * and inline reassignment that updates the totals. Scopeable to one project.
 */
function WorkloadView() {
  const qc = useQueryClient();
  const unit = useEffortUnit();
  const [scope, setScope] = useState<string>("all");

  const wl = useQuery({
    queryKey: queryKeys.teamWorkload(),
    queryFn: api.teamWorkload,
  });
  const tasksQ = useQuery({
    queryKey: ["team", "tasks"],
    queryFn: api.teamTasks,
  });
  const allocQ = useQuery({
    queryKey: ["allocations"],
    queryFn: api.listAllocations,
  });
  const teamsQ = useQuery({ queryKey: ["teams"], queryFn: () => api.teams() });
  // Current total allocation % per member (allocations covering today).
  const allocPct = useMemo(() => {
    const m = new Map<string, number>();
    const today = new Date().toISOString().slice(0, 10);
    for (const a of allocQ.data?.allocations ?? []) {
      if (a.startDate <= today && today <= a.endDate)
        m.set(a.userId, (m.get(a.userId) ?? 0) + a.percent);
    }
    return m;
  }, [allocQ.data]);

  const reassign = useMutation({
    mutationFn: ({
      taskId,
      assigneeId,
    }: {
      taskId: string;
      assigneeId: string;
    }) => api.patchTask(taskId, { assigneeId: assigneeId || null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team", "tasks"] }),
  });

  const members = wl.data?.members ?? [];
  const allTasks = tasksQ.data?.tasks ?? [];
  const tasks = useMemo(
    () =>
      scope === "all"
        ? allTasks
        : allTasks.filter((t) => t.projectId === scope),
    [allTasks, scope],
  );

  const projects = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of allTasks) m.set(t.projectId, t.projectName);
    return [...m.entries()];
  }, [allTasks]);

  // Week columns (this week + next WEEKS-1).
  const weeks = useMemo(() => {
    const start = mondayOf(new Date());
    return Array.from({ length: WEEKS }, (_, i) => {
      const d = new Date(start.getTime() + i * 7 * DAY);
      return { key: weekKey(d), label: `${d.getMonth() + 1}/${d.getDate()}` };
    });
  }, []);

  // member -> weekKey -> committed effort
  const grid = useMemo(() => {
    const g = new Map<string, Map<string, number>>();
    for (const t of tasks) {
      if (!t.assigneeId || t.estimate == null) continue;
      const due = t.dueDate ? parseDateLocal(t.dueDate) : null;
      const key = due ? weekKey(due) : weeks[0]!.key; // undated → this week
      let row = g.get(t.assigneeId);
      if (!row) g.set(t.assigneeId, (row = new Map()));
      row.set(key, (row.get(key) ?? 0) + t.estimate);
    }
    return g;
  }, [tasks, weeks]);

  const cap = (m: (typeof members)[number]) =>
    m.capacity && m.capacity > 0 ? m.capacity : null;

  // Utilisation trend: total committed effort vs total capacity per week.
  const totalCapacity = members.reduce((s, m) => s + (m.capacity ?? 0), 0);
  const trend = weeks.map((w) => {
    const effort = members.reduce(
      (s, m) => s + (grid.get(m.userId)?.get(w.key) ?? 0),
      0,
    );
    return totalCapacity > 0 ? Math.round((effort / totalCapacity) * 100) : 0;
  });

  // Per-team utilisation: committed effort vs capacity, summed over a team's
  // members (uses current committed effort, not the weekly buckets).
  const memberById = new Map(members.map((m) => [m.userId, m]));
  const teamUtil = (teamsQ.data?.teams ?? [])
    .map((t) => {
      let effort = 0;
      let capacity = 0;
      for (const tm of t.members ?? []) {
        const m = memberById.get(tm.userId);
        if (!m) continue;
        effort += m.committedEffort;
        capacity += m.capacity ?? 0;
      }
      return {
        id: t.id,
        name: t.name,
        effort,
        capacity,
        pct: capacity > 0 ? Math.round((effort / capacity) * 100) : null,
      };
    })
    .filter((t) => t.capacity > 0 || t.effort > 0);

  return (
    <div className="pg-wrap" style={{ padding: "22px 30px 60px" }}>
      <div className="rpt-head" style={{ marginBottom: 4 }}>
        <h1 className="pg-title" style={{ margin: 0 }}>
          Workload balancing
        </h1>
        <select
          className="select"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          aria-label="Scope to project"
          style={{ width: 200 }}
        >
          <option value="all">All projects</option>
          {projects.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      </div>
      <p className="t-sm t-muted">
        Committed effort ({unit}) per member per week, bucketed by due date.
        Cells over a member&rsquo;s weekly capacity are flagged red.
      </p>

      {wl.isLoading || tasksQ.isLoading ? (
        <Skeleton count={6} height={36} />
      ) : (
        <div className="wl-heatmap" style={{ marginTop: 14 }}>
          <table className="wl-table">
            <thead>
              <tr>
                <th className="wl-name-h">Member</th>
                {weeks.map((w) => (
                  <th key={w.key} className="wl-week-h">
                    {w.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const row = grid.get(m.userId);
                const c = cap(m);
                return (
                  <tr key={m.userId}>
                    <td className="wl-name">
                      <Avatar id={m.userId} name={m.name} size={22} />
                      <span>{m.name}</span>
                      {c != null && (
                        <span className="t-xs t-muted"> · {c}/wk</span>
                      )}
                      {allocPct.get(m.userId) ? (
                        <span
                          className="t-xs"
                          style={{
                            color:
                              (allocPct.get(m.userId) ?? 0) > 100
                                ? "var(--st-danger-ink, #dc2626)"
                                : "var(--muted)",
                          }}
                          title="Current resource allocation across projects"
                        >
                          {" "}
                          · {allocPct.get(m.userId)}% alloc
                        </span>
                      ) : null}
                    </td>
                    {weeks.map((w) => {
                      const e = row?.get(w.key) ?? 0;
                      const over = c != null && e > c;
                      const ratio = c ? Math.min(1, e / c) : e > 0 ? 0.5 : 0;
                      return (
                        <td
                          key={w.key}
                          className={`wl-cell${over ? " over" : ""}`}
                          style={{
                            background: over
                              ? undefined
                              : e === 0
                                ? "transparent"
                                : `rgba(37,99,235,${0.12 + ratio * 0.5})`,
                          }}
                          title={
                            over
                              ? `${e} ${unit} — over by ${(e - c!).toFixed(0)}`
                              : `${e} ${unit}`
                          }
                        >
                          {e > 0 ? e : ""}
                          {over ? ` (+${(e - c!).toFixed(0)})` : ""}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalCapacity > 0 && (
            <div className="wl-trend">
              <h3 className="rpt-h" style={{ marginTop: 24 }}>
                Utilisation trend (committed ÷ capacity per week)
              </h3>
              <div className="wl-trend-row">
                {weeks.map((w, i) => (
                  <div key={w.key} className="wl-trend-cell">
                    <div className="wl-trend-bar-wrap">
                      <div
                        className={`wl-trend-bar${trend[i]! > 100 ? " over" : ""}`}
                        style={{ height: `${Math.min(100, trend[i]!)}%` }}
                      />
                    </div>
                    <span className="t-xs">{trend[i]}%</span>
                    <span className="t-xs t-muted">{w.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {teamUtil.length > 0 && (
            <div className="wl-teamutil">
              <h3 className="rpt-h" style={{ marginTop: 24 }}>
                Team utilisation
              </h3>
              <ul className="wl-teamutil-list">
                {teamUtil.map((t) => {
                  const over = t.pct != null && t.pct > 100;
                  const under = t.pct != null && t.pct < 50;
                  return (
                    <li key={t.id} className="wl-teamutil-row">
                      <span style={{ width: 140 }}>{t.name}</span>
                      <div className="cap-bar" style={{ flex: 1 }}>
                        <div
                          className={`cap-fill${over ? " over" : ""}${under ? " under" : ""}`}
                          style={{
                            width: `${t.pct == null ? 0 : Math.min(100, t.pct)}%`,
                          }}
                        />
                      </div>
                      <span className="t-sm" style={{ width: 130 }}>
                        {t.effort} / {t.capacity || "—"} {unit}
                        {t.pct != null ? ` · ${t.pct}%` : ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <h3 className="rpt-h" style={{ marginTop: 28 }}>
            Reassign work
          </h3>
          <p className="t-sm t-muted">
            Move a task to a different member to rebalance; the heatmap updates.
          </p>
          <ul className="wl-task-list">
            {tasks
              .filter((t) => t.estimate != null)
              .slice(0, 60)
              .map((t) => (
                <li key={t.id} className="wl-task-row">
                  <span className="wl-task-title">{t.title}</span>
                  <span className="t-xs t-muted">{t.projectName}</span>
                  <span className="t-xs">{formatEffort(t.estimate, unit)}</span>
                  <select
                    className="select"
                    value={t.assigneeId ?? ""}
                    onChange={(e) =>
                      reassign.mutate({
                        taskId: t.id,
                        assigneeId: e.target.value,
                      })
                    }
                    aria-label={`Reassign ${t.title}`}
                    style={{ width: 160 }}
                  >
                    <option value="">Unassigned</option>
                    {members.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/workload")({
  component: WorkloadView,
});
